import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Badge from '@pelckmans/elementis/components/badge';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import Tabs from '@pelckmans/elementis/components/tabs';
import TrashIcon from '@pelckmans/elementis/icons/trash';

import { Group, GroupStatus } from '../../../../../api/groups';
import { fetchLinkedStudentModules, GroupModuleYearUnwind } from '../../../../../api/my-groups';
import ModalContext from '../../../../../contexts/ModalContext';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import { buildGroupProps } from '../../../../../utils/elementisUtils';
import { deleteGroup, updateGroup } from '../../../api/MyGroupsApi';
import { SchoolYear } from '../../../hooks/useGroups';
import RemoveGroupConfirmationModal from '../../remove-group-confirmation/RemoveGroupConfirmationModal';
import EditGroupAvailability from './components/EditGroupAvailability.vo';
import EditGroupForm from './components/EditGroupInfo.vo';
import EditGroupModalMaterial from './components/EditGroupModalMaterial.vo';
import EditGroupMembers from './components/EditGroupMembers.vo';
import UnconfirmedChangesDialog from '../../../../../shared-components/modals/UnconfirmedChangesDialog';
import { useDefaultToast } from '../../../../../hooks/useDefaultToast';
import EditGroupAlerts from './components/EditGroupAlerts';

interface Props {
  group: Group;
  id: string;
  onGroupEdited: () => void;
  selectedSchoolYear: SchoolYear;
  nextSchoolYear?: {
    from: Date;
    to: Date;
  };
}

export interface FormValues {
  name: string;
  startDate: string;
  endDate: string;
  totalAccepted: number;
  status: GroupStatus;
  linkedModulesCount: number;
}

const TABS = {
  INFO: 'INFO',
  MEMBERS: 'MEMBERS',
  MATERIAL: 'MATERIAL',
} as const;

export type Tab = typeof TABS[keyof typeof TABS];

export default function EditGroupModal({ id, group, selectedSchoolYear, onGroupEdited, nextSchoolYear }: Props) {
  const { t } = useTranslation();
  const { closeModal, showModal } = useContext(ModalContext);
  const handleError = useThrowAsyncError();
  const [selectedTab, onTabChange] = useState<Tab>(TABS.INFO);
  const [linkedModules, setLinkedModules] = useState<GroupModuleYearUnwind[]>([]);
  const { failedToast, successToast } = useDefaultToast();
  const [currentGroupValues, setCurrentGroupValues] = useState(group);

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: {
      name: currentGroupValues.name,
      startDate: currentGroupValues.startDate,
      endDate: currentGroupValues.endDate,
      totalAccepted: currentGroupValues.totalAccepted,
      status: currentGroupValues.status!,
    },
  });
  const isDirty = Object.keys(methods.formState.dirtyFields).length > 0;
  const totalAccepted = methods.watch('totalAccepted');
  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (data: FormValues) => {
    try {
      const modifiedEndDate = methods.formState.dirtyFields.endDate;

      const updatedGroup = await updateGroup(selectedSchoolYear, currentGroupValues.id, {
        name: data.name,
        endDate: modifiedEndDate ? data.endDate : undefined,
        status: modifiedEndDate && data.status === 'published' ? 'published' : undefined,
      });

      methods.reset({ ...updatedGroup, linkedModulesCount: linkedModules.length });
      setCurrentGroupValues(updatedGroup);
      successToast();
      onGroupEdited();
    } catch (err) {
      failedToast();
    }
  };

  const onDelete = async () => {
    try {
      closeModal('delete-vo-group-confirmation');
      await deleteGroup(selectedSchoolYear, group.id);
      closeModal(id);
      onGroupEdited();
    } catch (err) {
      failedToast();
    }
  };

  const handleDelete = async () => {
    if (group.status === 'unpublished') {
      await onDelete();
    } else {
      showModal(
        <RemoveGroupConfirmationModal
          id="delete-vo-group-confirmation"
          schoolYear={selectedSchoolYear}
          group={currentGroupValues}
          onConfirm={() => onDelete()}
        />,
      );
    }
  };

  const changeTab = useCallback(
    (tabId: Tab) => {
      if (tabId !== TABS.INFO) {
        methods.reset({ ...currentGroupValues, totalAccepted, linkedModulesCount: linkedModules.length });
      }

      onTabChange(tabId);
    },
    [currentGroupValues, onTabChange, methods, linkedModules, totalAccepted],
  );

  const handleTabChange = useCallback(
    (tab: Tab) => {
      if (tab === selectedTab) return;

      if (isDirty) {
        showModal(<UnconfirmedChangesDialog id="unconfirmed-changes-dialog" onConfirm={() => changeTab(tab)} />);
      } else {
        changeTab(tab);
      }
    },
    [isDirty, selectedTab, changeTab, showModal],
  );

  const handleClose = useCallback(() => {
    if (isDirty) {
      showModal(
        <UnconfirmedChangesDialog
          id="unconfirmed-changes-dialog"
          onConfirm={() => {
            onGroupEdited();
            closeModal(id);
          }}
        />,
      );
    } else {
      onGroupEdited();
      closeModal(id);
    }
  }, [isDirty, showModal, closeModal, id, onGroupEdited]);

  const onError = () => {
    if (formRef?.current?.parentElement?.scrollTo) {
      formRef.current.parentElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const fetchModules = async () => {
      const modules = await fetchLinkedStudentModules(group.id);
      setLinkedModules(modules);
      methods.setValue('linkedModulesCount', modules.length);
    };

    fetchModules().catch(handleError);
  }, [group.id, handleError, methods]);

  return (
    <Dialog id={id} aria-label={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.A11Y_LABEL')} modal size="sm">
      <Tabs selectedTab={selectedTab} onTabChange={tab => handleTabChange(tab as Tab)}>
        <Dialog.Header
          aria-label={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.CLOSE.A11Y_LABEL')}
          onClose={() => handleClose()}
          title={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.TITLE', {
            courseName: currentGroupValues?.name,
          })}
        >
          <Tabs.TabList aria-label={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.TABS.A11Y_LABEL')} fluid>
            <Tabs.Tab id={TABS.INFO}>{t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.TABS.INFO_AND_AVAILABILITY')}</Tabs.Tab>
            <Tabs.Tab id={TABS.MEMBERS} badge={<Badge>{totalAccepted}</Badge>}>
              {t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.TABS.MEMBERS')}
            </Tabs.Tab>
            <Tabs.Tab id={TABS.MATERIAL} badge={<Badge>{linkedModules.length}</Badge>}>
              {t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.TABS.MATERIAL')}
            </Tabs.Tab>
          </Tabs.TabList>
        </Dialog.Header>

        <Dialog.Body>
          <form ref={formRef} id="form-edit-group" onSubmit={methods.handleSubmit(onSubmit, onError)}>
            <FormProvider {...methods}>
              <Tabs.TabsContent>
                <Tabs.TabPanel id={TABS.INFO}>
                  <EditGroupAlerts />
                  <EditGroupForm
                    group={currentGroupValues}
                    selectedSchoolyear={selectedSchoolYear}
                    linkedModules={linkedModules}
                  />
                  <EditGroupAvailability
                    group={currentGroupValues}
                    selectedSchoolyear={selectedSchoolYear}
                    nextSchoolYear={nextSchoolYear}
                  />
                </Tabs.TabPanel>
                <Tabs.TabPanel id={TABS.MEMBERS}>
                  <EditGroupMembers group={currentGroupValues} selectedSchoolYear={selectedSchoolYear} />
                </Tabs.TabPanel>
                <Tabs.TabPanel id={TABS.MATERIAL}>
                  <EditGroupModalMaterial
                    linkedModules={linkedModules}
                    group={currentGroupValues}
                    onModulesAdded={modules => {
                      setLinkedModules(modules);
                      methods.setValue('linkedModulesCount', modules.length);
                    }}
                    schoolyear={selectedSchoolYear}
                  />
                </Tabs.TabPanel>
              </Tabs.TabsContent>
            </FormProvider>
          </form>
        </Dialog.Body>
        <Dialog.Footer
          buttonGroupPropsLeft={
            selectedTab === TABS.INFO
              ? buildGroupProps(
                  <Button prominence="danger" key="delete" onClick={() => handleDelete()} startIcon={<TrashIcon />}>
                    {t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.DELETE.LABEL')}
                  </Button>,
                )
              : undefined
          }
          buttonGroupPropsRight={buildGroupProps(
            <>
              {selectedTab === TABS.INFO && (
                <Fragment key="info-buttons">
                  <Button
                    prominence="neutral"
                    key="cancel"
                    onClick={() => {
                      handleClose();
                    }}
                  >
                    {t('ACTIONS.CLOSE')}
                  </Button>
                  {selectedTab === TABS.INFO && (
                    <Button prominence="success" key="create" type="submit" form="form-edit-group">
                      {t('ACTIONS.SAVE')}
                    </Button>
                  )}
                </Fragment>
              )}

              {selectedTab !== TABS.INFO && (
                <Button
                  prominence="neutral"
                  key="cancel"
                  onClick={() => {
                    handleClose();
                  }}
                >
                  {t('ACTIONS.CLOSE')}
                </Button>
              )}
            </>,
          )}
        />
      </Tabs>
    </Dialog>
  );
}
